import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { SiaLayoutService, SiaTranslatePipe, notify } from '@sia/shared';
import { AuthComponent } from '../../auth.component';

@Component({
  selector: 'app-login',
  templateUrl: './auth-login-2FA.component.html',
  styleUrls: ['./auth-login-2FA.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatButton,
    RouterModule,
    MatProgressSpinner,
    SiaTranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthLogin2FAComponent
  extends AuthComponent
  implements OnInit, OnDestroy
{
  #layoutService = inject(SiaLayoutService);
  #fb = inject(FormBuilder);

  loginForm: FormGroup = new FormGroup({
    userIdentifier: new FormControl(''),
  });

  credentials = signal<any>({});
  minLength = signal(5);
  maxLength = signal(20);

  qrCodeString = signal('');
  qrCodeVisible = signal(false);
  totpPin = signal('');

  hide = signal(true);
  twoFactorType = signal('');
  enableOtp = signal(false);

  email = signal('');

  @HostBinding('class') hostClasses = 'w-full px-4 lg:px-0';

  constructor() {
    super();
    effect(() => {
      if (!this.toUpdate()) {
        this.tryTokenLogin();
      }
    });
  }

  async ngOnInit() {
    super.ngOnInit();
    this.contentReady.set('input');

    const isTokenExpired = await this.tokenStore.isCurrentTokenExpired();
    if (isTokenExpired) {
      await this.clearSession();
    }

    this.loginForm = this.#createSignupForm();
  }

  #createSignupForm(): FormGroup {
    const emailOrPhoneValidator = (control: FormControl) => {
      const value = control.value;
      if (!value) return { required: true };
      const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (emailRegex.test(value)) {
        return null;
      }
      return { emailOrPhone: true };
    };
    return this.#fb.group({
      userIdentifier: [null, [emailOrPhoneValidator, Validators.required]],
    });
  }

  async sendNewPin() {
    this.authService
      .getNewPinOtp(this.credentials().userIdentifier)
      .then(async (result: any) => {
        if (result) {
          notify('Nuovo pin inviato', 1000, 'success');
          this.startCountDown(180);
        }
      })
      .catch((e: any) => {});
  }

  async login() {
    if (this.loginStarted()) {
      return;
    }

    if (!this.loginForm || this.loginForm.invalid) {
      notify('Compila tutti i campi', 2000, 'error');
      return;
    }
    if (this.twoFactorType() && this.isOtpInvalid()) {
      notify('Pin errato', 2000, 'error');
      return;
    }

    await this.forceClearSession();

    this.startCountDown(180);
    this.loginStarted.set(true);

    const creds = this.loginForm.value;
    creds.otp = this.otp();
    this.credentials.set(creds);

    try {
      const result: any = await this.authService.login(creds);
      if (result.twoFactorType) {
        this.twoFactorType.set(result.twoFactorType);
      } else if (result.success) {
        notify('Login effettuato. Attendere', 1000, 'success');
        this.stopCountDown();
        await this.#layoutService.getAllLayouts();
        await this.loginAfterTokenReceived(result);
      }
    } catch (e: any) {
    } finally {
      this.loginStarted.set(false);
    }
  }

  @HostListener('window:unload')
  override ngOnDestroy() {
    super.ngOnDestroy();
    this.stopCountDown();
    this.credentials.set(null);
    var form = document.getElementById('authForm');
    if (form) form.remove();
  }

  onOtpChange(value: string) {
    this.otp.set(value);
    if (!this.isOtpInvalid()) {
      this.enableOtp.set(true);
    }
  }

  reset2FALogin() {
    this.twoFactorType.set('');
    this.otp.set('');
    this.loginStarted.set(false);
    this.credentials.set({});
    this.loginForm.reset();
    this.contentReady.set('input');
    this.resetCountdown();
  }
}
